
import React, { useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertTriangle, Info, CheckCircle } from "lucide-react";
import NumericInput from "@/components/ui/NumericInput";
import { formatPercentage, parsePercentage } from "../../utils/formatters";
import { validateSellerCredit } from "../../utils/sellerCreditValidation";
import { calculateSellerCreditAmount, validateSellerCreditInput } from "../../utils/sellerCreditCalculations";
import type { MortgageData } from "../../CalculatorTabs";
import { cn } from "@/lib/utils";

interface EnhancedSellerCreditSectionProps {
  mortgageData: MortgageData;
  onSellerCreditChange: (value: number) => void;
}

export const EnhancedSellerCreditSection: React.FC<EnhancedSellerCreditSectionProps> = ({
  mortgageData,
  onSellerCreditChange
}) => {
  const validation = useMemo(() => {
    const totalClosingCostsBeforeCredit = 
      mortgageData.closingCosts.loanOrigination +
      mortgageData.closingCosts.appraisalFee +
      mortgageData.closingCosts.titleServices +
      mortgageData.closingCosts.governmentRecordingCharges +
      mortgageData.closingCosts.prepaids +
      mortgageData.closingCosts.initialEscrow +
      mortgageData.closingCosts.other +
      (mortgageData.closingCosts.fundingFee || 0);
    
    return validateSellerCredit(
      mortgageData.sellerCredit,
      mortgageData.homePrice,
      totalClosingCostsBeforeCredit,
      mortgageData.loanType,
      mortgageData.downPaymentPercentage
    );
  }, [
    mortgageData.sellerCredit,
    mortgageData.homePrice,
    mortgageData.closingCosts,
    mortgageData.loanType,
    mortgageData.downPaymentPercentage
  ]);

  // Enhanced input validation
  const inputValidation = useMemo(() => {
    return validateSellerCreditInput(mortgageData.sellerCredit, mortgageData.homePrice);
  }, [mortgageData.sellerCredit, mortgageData.homePrice]);

  // Calculate actual seller credit amount with enhanced validation
  const sellerCreditCalculation = useMemo(() => {
    const totalClosingCostsBeforeCredit = 
      mortgageData.closingCosts.loanOrigination +
      mortgageData.closingCosts.appraisalFee +
      mortgageData.closingCosts.titleServices +
      mortgageData.closingCosts.governmentRecordingCharges +
      mortgageData.closingCosts.prepaids +
      mortgageData.closingCosts.initialEscrow +
      mortgageData.closingCosts.other +
      (mortgageData.closingCosts.fundingFee || 0);

    const result = calculateSellerCreditAmount(
      mortgageData.sellerCredit,
      mortgageData.homePrice,
      totalClosingCostsBeforeCredit
    );

    console.log('EnhancedSellerCreditSection - Calculation result:', {
      percentage: mortgageData.sellerCredit,
      homePrice: mortgageData.homePrice,
      closingCosts: totalClosingCostsBeforeCredit,
      effectiveAmount: result.effectiveDollarAmount,
      storedAmount: mortgageData.closingCosts.sellerCreditAmount
    });

    return result;
  }, [
    mortgageData.sellerCredit,
    mortgageData.homePrice,
    mortgageData.closingCosts
  ]);

  // Enhanced change handler with validation
  const handleSellerCreditChange = (value: number) => {
    const validation = validateSellerCreditInput(value, mortgageData.homePrice);
    
    if (validation.isValid) {
      onSellerCreditChange(validation.safeValue);
    }
  };

  const getValidationIcon = () => {
    if (!inputValidation.isValid || !validation.isValid) {
      return <AlertTriangle className="w-4 h-4 text-red-500" />;
    }
    if (validation.isWarning) {
      return <AlertTriangle className="w-4 h-4 text-orange-500" />;
    }
    if (mortgageData.sellerCredit > 0) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    return null;
  };

  const getInputClassName = () => {
    if (!inputValidation.isValid || !validation.isValid) {
      return "border-red-500 focus:border-red-500 focus:ring-red-500/20";
    }
    if (validation.isWarning) {
      return "border-orange-500 focus:border-orange-500 focus:ring-orange-500/20";
    }
    if (mortgageData.sellerCredit > 0) {
      return "border-green-500 focus:border-green-500 focus:ring-green-500/20";
    }
    return "";
  };

  // Use the calculated amount if it's valid and different from stored amount
  const displayAmount = sellerCreditCalculation.isValid ? 
    sellerCreditCalculation.effectiveDollarAmount : 
    Math.max(0, mortgageData.closingCosts.sellerCreditAmount);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium">Seller Credit</Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-4 h-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <div className="space-y-2">
                  <p className="font-medium">Seller Credits</p>
                  <p className="text-sm">
                    Seller credits reduce your closing costs. Limits vary by loan type and down payment:
                  </p>
                  <ul className="text-sm space-y-1">
                    <li>• Conventional: 3-9% (based on down payment)</li>
                    <li>• FHA: Up to 6%</li>
                    <li>• VA: Up to 4%</li>
                    <li>• USDA: Up to 6%</li>
                  </ul>
                  <p className="text-xs text-muted-foreground">
                    Excess credit cannot reduce principal balance.
                  </p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        <div className="flex items-center gap-2">
          {getValidationIcon()}
          <NumericInput
            value={mortgageData.sellerCredit}
            onChange={handleSellerCreditChange}
            formatValue={formatPercentage}
            parseValue={parsePercentage}
            min={0}
            max={Math.min(validation.maxAllowed, 10)}
            step={0.1}
            className={cn("w-24", getInputClassName())}
            inputClassName="text-right"
            suffix="%"
          />
        </div>
      </div>

      {/* Enhanced validation messages */}
      {(!inputValidation.isValid || !validation.isValid || validation.isWarning) && (
        <div className={cn(
          "flex items-start gap-2 p-3 rounded-lg text-sm",
          (!inputValidation.isValid || !validation.isValid) && "bg-red-50 text-red-700 border border-red-200",
          validation.isWarning && !(!inputValidation.isValid || !validation.isValid) && "bg-orange-50 text-orange-700 border border-orange-200"
        )}>
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{!inputValidation.isValid ? inputValidation.message : validation.message}</span>
        </div>
      )}

      {/* Credit Amount Display - only show if meaningful amount */}
      {mortgageData.sellerCredit > 0 && displayAmount > 0 && (
        <div className="space-y-2">
          <div className="flex justify-between items-center p-3 bg-green-50 border border-green-200 rounded-lg">
            <span className="text-sm font-medium text-green-900">Credit Amount:</span>
            <span className="text-sm font-bold text-green-900">
              ${displayAmount.toLocaleString()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
